import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { coreApi } from "@/core/services/coreApi";
import { sync } from "@/core/sync";
import { CORE_EVENTS, makeEvent } from "@/core/sync/events";

/**
 * hotelStore
 *
 * Holds the single hotel's branding for this device. The store is:
 *   - Persisted locally (localStorage) so the app renders instantly on cold start.
 *   - Hydrated from the backend on mount (via `hydrateFromBackend`).
 *   - Kept in sync between Host and Projector windows via BroadcastChannel
 *     using CORE_EVENTS.HOTEL_BRANDING_UPDATED.
 *
 * Every persistent mutation:
 *   1. Optimistically updates local state.
 *   2. Broadcasts CORE_EVENTS.HOTEL_BRANDING_UPDATED so other windows react.
 *   3. Debounces a PUT /api/hotel so the backend becomes source of truth
 *      the next time the app boots.
 */

const defaultHotel = {
  hotelId: "local",
  name: "Your Hotel",
  logoUrl: null,
  welcomeMessage: "Welcome to Bingo Night",
  themeColor: "#F59E0B",
  welcomeBackground: {
    type: "particles", // "video" | "image" | "particles"
    assetUrl: null,
    presetKey: "cinematic-particles",
  },
  backgroundMusicUrl: null,
  masterVolume: 0.8,
  sfxEnabled: true,
};

// Debounced backend sync (fire-and-forget, 500ms after last change).
let saveTimer = null;
let pendingPatch = {};

const scheduleSave = () => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    const patch = pendingPatch;
    pendingPatch = {};
    saveTimer = null;
    if (!patch || Object.keys(patch).length === 0) return;
    try {
      await coreApi.putHotel(patch);
    } catch (e) {
      console.warn(
        "[hotelStore] save failed",
        e?.userMessage || e?.message || e,
      );
    }
  }, 500);
};

const mergePatch = (patch) => {
  pendingPatch = { ...pendingPatch, ...patch };
};

const broadcast = (patch) => {
  sync.publish(
    makeEvent(CORE_EVENTS.HOTEL_BRANDING_UPDATED, patch, "core"),
  );
};

export const useHotelStore = create(
  persist(
    (set, get) => ({
      hotel: defaultHotel,
      hydrated: false,

      /**
       * Merge a partial patch into the hotel state. Broadcasts + persists.
       * Set { silent: true } to skip broadcast + save (used by the Projector
       * when it receives an event from the Host — otherwise we'd echo loop).
       */
      setHotel: (patch, opts = {}) => {
        set((state) => ({ hotel: { ...state.hotel, ...patch } }));
        if (!opts.silent) {
          broadcast(patch);
          mergePatch(patch);
          scheduleSave();
        }
      },

      setThemeColor: (themeColor) => {
        get().setHotel({ themeColor });
      },

      setWelcomeBackground: (welcomeBackground) => {
        set((state) => ({
          hotel: {
            ...state.hotel,
            welcomeBackground: {
              ...state.hotel.welcomeBackground,
              ...welcomeBackground,
            },
          },
        }));
        broadcast({ welcomeBackground });
        mergePatch({ welcomeBackground });
        scheduleSave();
      },

      resetHotel: () => {
        set({ hotel: defaultHotel });
        broadcast(defaultHotel);
        mergePatch(defaultHotel);
        scheduleSave();
      },

      /**
       * Fetch hotel from backend and hydrate local state.
       * Called on app boot from ThemeService.
       */
      hydrateFromBackend: async () => {
        try {
          const doc = await coreApi.getHotel();
          if (!doc) return;
          set((state) => ({
            hotel: {
              ...state.hotel,
              hotelId: doc.hotelId ?? state.hotel.hotelId,
              name: doc.name ?? state.hotel.name,
              logoUrl: doc.logoUrl ?? state.hotel.logoUrl,
              welcomeMessage:
                doc.welcomeMessage ?? state.hotel.welcomeMessage,
              themeColor: doc.themeColor ?? state.hotel.themeColor,
              welcomeBackground:
                doc.welcomeBackground ?? state.hotel.welcomeBackground,
              backgroundMusicUrl:
                doc.backgroundMusicUrl ?? state.hotel.backgroundMusicUrl,
              masterVolume:
                doc.masterVolume ?? state.hotel.masterVolume,
              sfxEnabled:
                doc.sfxEnabled ?? state.hotel.sfxEnabled,
            },
            hydrated: true,
          }));
        } catch {
          // Backend unreachable: keep local defaults + set hydrated so UI proceeds.
          set({ hydrated: true });
        }
      },
    }),
    {
      name: "ic-bingo-hotel",
      storage: createJSONStorage(() => localStorage),
      version: 2,
      partialize: (state) => ({ hotel: state.hotel }),
    },
  ),
);
